/* eslint-disable react/no-unused-prop-types */

import { MediumHeader } from '@narvar/nth-bit-header';
import TextLink from '@narvar/nth-bit-text-link';
import { RetailerTheme } from '@narvar/nth-hook-loma';
import { useTheme, withThemeProvider } from '@narvar/nth-hook-theme';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import React, { CSSProperties, useState } from 'react';
import { ParamScheduleDeliveryCarrier } from '../../../../server/api/schema';
import {
  StyledColumnDivider,
  StyledDeliveryInstructionsContainer,
  StyledRescheduleLink,
  StyledRescheduleMobileLink,
  StyledScheduledInfoContainer,
  StyledScheduledInfoGrid,
  StyledScheduledInfoWrapper,
} from '../styled';
import RescheduleLink from './RescheduleLInk';
import ScheduledDeliveryInstructions from './ScheduledDeliveryInstructions';
import 'dayjs/locale/ja';
import { useTranslator } from '../utils/translator';

dayjs.extend(advancedFormat);

interface ScheduledInfoPanelProps {
  scheduledDate?: Date;
  scheduledTime?: string;
  featureName?: string;
  carrier?: ParamScheduleDeliveryCarrier;
  rescheduleEnabled: boolean;
  onRescheduleClick: () => void;
  localeLanguage?: string;
}

const ScheduledInfoPanel = ({
  featureName,
  scheduledDate,
  scheduledTime,
  rescheduleEnabled,
  onRescheduleClick,
  localeLanguage,
}: ScheduledInfoPanelProps) => {
  const [showDetails, setShowDetails] = useState(false);

  const { t, tc } = useTranslator();
  const scheduleDeliveryScheduleDeliveryHeader = t(
    'You’ve Scheduled Your Delivery',
  );
  const scheduleDeliveryInstructionsContentForCarrier = tc(
    'scheduleDeliveryInstructionsContentForCarrier',
  );

  const scheduleDeliveryShowDetails = t('Show Details');
  const scheduleDeliveryHideDetails = t('Hide Details');

  const handleRescheduleClick = () => {
    onRescheduleClick();
  };

  const handleDetailsClick = () => {
    setShowDetails(!showDetails);
  };

  const { bodytextDefaultNone: bodyText, linksNoneDefault: linksDefault } =
    useTheme() as {
      [key in keyof RetailerTheme]: CSSProperties;
    };

  if (!scheduledDate) return null;

  return (
    <StyledScheduledInfoContainer>
      <StyledScheduledInfoWrapper>
        <MediumHeader containerCustomStyle={{ marginBottom: '15px' }}>
          {scheduleDeliveryScheduleDeliveryHeader}
        </MediumHeader>
        {!showDetails && rescheduleEnabled && (
          <StyledRescheduleMobileLink>
            <RescheduleLink
              featureName={featureName}
              onClick={handleRescheduleClick}
            />
          </StyledRescheduleMobileLink>
        )}
        <div>
          {showDetails && (
            <StyledScheduledInfoGrid>
              <div>
                <MediumHeader>
                  {dayjs(scheduledDate)
                    .locale(localeLanguage || 'en')
                    .format('dddd, MMMM D')}
                </MediumHeader>
                {scheduledTime && (
                  <span style={{ ...bodyText }}>{scheduledTime}</span>
                )}
              </div>
              <StyledColumnDivider />
              <StyledDeliveryInstructionsContainer>
                <ScheduledDeliveryInstructions
                  featureName={featureName}
                  rescheduleEnabled={rescheduleEnabled}
                  additionalInstructions={
                    scheduleDeliveryInstructionsContentForCarrier
                  }
                  onRescheduleClick={onRescheduleClick}
                />
              </StyledDeliveryInstructionsContainer>
            </StyledScheduledInfoGrid>
          )}
          <TextLink
            onClick={handleDetailsClick}
            linkStyle={{ sDefault: { ...bodyText, ...linksDefault } }}
          >
            {showDetails
              ? scheduleDeliveryHideDetails
              : scheduleDeliveryShowDetails}
          </TextLink>
        </div>
      </StyledScheduledInfoWrapper>
      {!showDetails && rescheduleEnabled && (
        <StyledRescheduleLink>
          <RescheduleLink
            featureName={featureName}
            onClick={handleRescheduleClick}
          />
        </StyledRescheduleLink>
      )}
    </StyledScheduledInfoContainer>
  );
};

export default withThemeProvider(ScheduledInfoPanel, {
  themeTokens: ['bodytextDefaultNone', 'linksNoneDefault'],
});
